import React, { useState, useEffect, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import {
  FaChartLine, FaChartPie, FaFilter,
  FaToolbox, FaMapMarkerAlt, FaListUl, FaChevronRight
} from 'react-icons/fa';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { format, subDays } from 'date-fns';

import DashboardLayout from '../../layouts/DashboardLayout';
import LoadingSpinner from '../../components/shared/LoadingSpinner';
import FeedbackModal from '../../components/shared/ModalNotification';
import OriginBadge from '../../components/shared/OriginBadge';
import { formatCurrency } from '../../utils/utils';
import { fetchJobsReport } from './jobsService';

const CATEGORY_COLORS = {
  'Crew Hours':  '#5b6ee6',
  'Materials':   '#2fbf71',
  'Products':    '#f59f00',
  'Maintenance': '#e74c3c',
  'Tools':       '#8e44ad',
  'Fuel':        '#16a085',
  'Others':      '#7f8c8d',
};
const FALLBACK_COLOR = '#95a5a6';

const STATUS_OPTIONS = ['Scheduled', 'In Progress', 'Completed'];

const PRESETS = [
  { id: '7',    label: 'Last 7 days',   days: 7 },
  { id: '15',   label: 'Last 15 days',  days: 15 },
  { id: '30',   label: 'Last 30 days',  days: 30 },
  { id: '60',   label: 'Last 60 days',  days: 60 },
  { id: '90',   label: 'Last 90 days',  days: 90 },
  { id: '180',  label: 'Last 180 days', days: 180 },
  { id: '365',  label: 'Last 1 year',   days: 365 },
  { id: 'custom', label: 'Custom range', days: null },
];

const SERVICE_COLORS = ['#3498DB', '#2ECC71', '#F39C12', '#E84393', '#9B59B6', '#16A085', '#E67E22', '#1ABC9C'];

const Page = styled.div`
  padding-bottom: 32px;
  color: ${p => p.theme.colors.textPrimary};
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 12px;
  h1 {
    font-size: 22px;
    display: flex;
    align-items: center;
    gap: 10px;
    margin: 0;
    color: ${p => p.theme.colors.textPrimary};
  }
  @media (max-width: 600px) {
    h1 { font-size: 18px; }
  }
`;

const Controls = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  align-items: center;
`;

const PresetSelect = styled.select`
  padding: 10px 14px;
  border-radius: 8px;
  background: ${p => p.theme.colors.surface};
  color: ${p => p.theme.colors.textPrimary};
  border: 1px solid ${p => p.theme.colors.inputBorder};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  outline: none;
  &:focus { border-color: ${p => p.theme.colors.primary}; }
`;

const DateInput = styled.input`
  padding: 10px 12px;
  border-radius: 8px;
  background: ${p => p.theme.colors.surface};
  color: ${p => p.theme.colors.textPrimary};
  border: 1px solid ${p => p.theme.colors.inputBorder};
  font-size: 14px;
  outline: none;
  &:focus { border-color: ${p => p.theme.colors.primary}; }
`;

const FilterBar = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px;
  background: ${p => p.theme.colors.surface};
  border-radius: 14px;
  padding: 16px;
  border: 1px solid rgba(255,255,255,0.05);
  box-shadow: 0 2px 10px rgba(0,0,0,0.2);
  margin-bottom: 18px;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  label {
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    font-weight: 600;
    color: ${p => p.theme.colors.textSecondary};
    opacity: 0.75;
    display: flex;
    align-items: center;
    gap: 6px;
    svg { color: ${p => p.theme.colors.primary}; }
  }
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  min-height: 30px;
  align-items: center;
`;

const Chip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  font-weight: 600;
  padding: 5px 10px;
  border-radius: 999px;
  cursor: pointer;
  border: 1px solid ${p => p.active ? (p.color || p.theme.colors.primary) : 'rgba(255,255,255,0.12)'};
  background: ${p => p.active ? `${p.color || p.theme.colors.primary}22` : 'transparent'};
  color: ${p => p.active ? (p.color || p.theme.colors.primary) : p.theme.colors.textSecondary};
  transition: all 0.15s ease;
  &:hover { border-color: ${p => p.color || p.theme.colors.primary}; }
`;

const MetricsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  gap: 12px;
  margin-bottom: 18px;
  @media (max-width: 600px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
  }
`;

const MetricCard = styled.div`
  background: ${p => p.theme.colors.surface};
  border-radius: 14px;
  padding: 16px 18px;
  border: 1px solid rgba(255,255,255,0.05);
  box-shadow: 0 2px 10px rgba(0,0,0,0.25);
  position: relative;
  overflow: hidden;
  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 3px;
    background: ${p => p.accent || p.theme.colors.primary};
  }
  .label {
    font-size: 10px;
    color: ${p => p.theme.colors.textSecondary};
    opacity: 0.7;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    font-weight: 600;
  }
  .value {
    font-size: 22px;
    font-weight: 700;
    color: ${p => p.valueColor || p.theme.colors.textPrimary};
    margin-top: 6px;
    letter-spacing: -0.3px;
    line-height: 1.15;
    word-break: break-word;
  }
  .sub {
    font-size: 11px;
    color: ${p => p.theme.colors.textSecondary};
    opacity: 0.55;
    margin-top: 3px;
  }
  @media (max-width: 600px) {
    padding: 14px;
    border-radius: 12px;
    .value { font-size: 18px; }
    .label { font-size: 9px; }
  }
`;

const Section = styled.div`
  background: ${p => p.theme.colors.surface};
  border-radius: 16px;
  border: 1px solid rgba(255,255,255,0.05);
  box-shadow: 0 2px 12px rgba(0,0,0,0.25);
  padding: 22px;
  margin-bottom: 14px;
  @media (max-width: 600px) {
    padding: 16px;
    border-radius: 14px;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 16px;
  h3 {
    font-size: 15px;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 8px;
    color: ${p => p.theme.colors.textPrimary};
    margin: 0;
    svg { color: ${p => p.theme.colors.primary}; }
  }
`;

const Toggle = styled.button`
  font-size: 12px;
  font-weight: 600;
  padding: 6px 12px;
  border-radius: 999px;
  cursor: pointer;
  border: 1px solid ${p => p.active ? p.theme.colors.primary : 'rgba(255,255,255,0.12)'};
  background: ${p => p.active ? `${p.theme.colors.primary}22` : 'transparent'};
  color: ${p => p.active ? p.theme.colors.primary : p.theme.colors.textSecondary};
`;

const DonutBody = styled.div`
  display: grid;
  grid-template-columns: minmax(240px, 0.9fr) minmax(0, 1.4fr);
  gap: 24px;
  align-items: center;
  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

const ChartArea = styled.div`
  position: relative;
  height: 260px;
`;

const CenterText = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  pointer-events: none;
  .label {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.8px;
    color: ${p => p.theme.colors.textSecondary};
    opacity: 0.7;
    font-weight: 600;
  }
  .value {
    font-size: 20px;
    font-weight: 700;
    color: ${p => p.theme.colors.textPrimary};
    margin-top: 4px;
  }
`;

const Legend = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const LegendRow = styled.div`
  display: grid;
  grid-template-columns: 14px minmax(0, 1fr) auto;
  align-items: center;
  gap: 10px;
  padding: 6px 0;
  border-bottom: 1px solid rgba(255,255,255,0.04);
  &:last-child { border-bottom: none; }
  .swatch {
    width: 12px;
    height: 12px;
    border-radius: 3px;
    background: ${p => p.color};
  }
  .name {
    font-size: 13px;
    color: ${p => p.theme.colors.textPrimary};
    font-weight: 500;
  }
  .amount {
    font-size: 13px;
    font-weight: 700;
    text-align: right;
    color: ${p => p.theme.colors.textPrimary};
    .pct {
      display: block;
      font-size: 10px;
      font-weight: 500;
      color: ${p => p.theme.colors.textSecondary};
      opacity: 0.7;
      margin-top: 1px;
    }
  }
`;

const BarRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  column-gap: 12px;
  row-gap: 4px;
  padding: 8px 0;
  border-bottom: 1px solid rgba(255,255,255,0.04);
  &:last-child { border-bottom: none; }
`;

const BarLabel = styled.div`
  grid-column: 1 / 2;
  grid-row: 1 / 2;
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${p => p.theme.colors.textPrimary};
  font-weight: 500;
  font-size: 13px;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const BarTrack = styled.div`
  grid-column: 1 / 2;
  grid-row: 2 / 3;
  height: 8px;
  background: rgba(255,255,255,0.06);
  border-radius: 999px;
  overflow: hidden;
`;

const BarFill = styled.div`
  height: 100%;
  background: ${p => p.color || p.theme.colors.primary};
  border-radius: 999px;
  width: ${p => p.width || '0%'};
  min-width: ${p => (p.width && p.width !== '0%') ? '3px' : '0'};
  transition: width 0.4s ease;
`;

const BarValue = styled.div`
  grid-column: 2 / 3;
  grid-row: 1 / 3;
  align-self: center;
  text-align: right;
  font-weight: 700;
  font-size: 14px;
  color: ${p => p.theme.colors.textPrimary};
  white-space: nowrap;
  .sub {
    display: block;
    font-size: 10px;
    font-weight: 500;
    color: ${p => p.theme.colors.textSecondary};
    opacity: 0.7;
    margin-top: 2px;
  }
`;

const TableWrap = styled.div`
  overflow-x: auto;
`;

const JobsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  th, td {
    text-align: left;
    padding: 10px 8px;
    border-bottom: 1px solid rgba(255,255,255,0.05);
    color: ${p => p.theme.colors.textPrimary};
  }
  th {
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    font-weight: 600;
    color: ${p => p.theme.colors.textSecondary};
    opacity: 0.75;
  }
  tbody tr {
    cursor: pointer;
    transition: background 0.15s ease;
  }
  tbody tr:hover { background: rgba(255,255,255,0.03); }
  td.num { text-align: right; font-weight: 600; white-space: nowrap; }
`;

const ServiceBadge = styled.span`
  display: inline-block;
  font-size: 10px;
  font-weight: 600;
  padding: 2px 7px;
  border-radius: 999px;
  background: ${p => p.color || '#3498DB'}22;
  color: ${p => p.color || '#3498DB'};
  border: 1px solid ${p => p.color || '#3498DB'}44;
  margin: 1px 3px 1px 0;
  white-space: nowrap;
`;

const Empty = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: ${p => p.theme.colors.textSecondary};
  opacity: 0.7;
  font-size: 14px;
`;

const serviceColor = (name) => {
  const s = String(name || '');
  let hash = 0;
  for (let i = 0; i < s.length; i++) hash = (hash * 31 + s.charCodeAt(i)) | 0;
  return SERVICE_COLORS[Math.abs(hash) % SERVICE_COLORS.length];
};

const computeDateRange = (presetId, customStart, customEnd) => {
  if (presetId === 'custom') {
    return { start_date: customStart || null, end_date: customEnd || null };
  }
  const preset = PRESETS.find(p => p.id === presetId);
  if (!preset || preset.days == null) return { start_date: null, end_date: null };
  const today = new Date();
  return {
    start_date: format(subDays(today, preset.days), 'yyyy-MM-dd'),
    end_date: format(today, 'yyyy-MM-dd'),
  };
};

const JobsReport = () => {
  const navigate = useNavigate();

  const [preset, setPreset] = useState('30');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  const [originFilter, setOriginFilter] = useState([]);
  const [serviceFilter, setServiceFilter] = useState([]);
  const [statusFilter, setStatusFilter] = useState(['Completed']);

  const [showPerJob, setShowPerJob] = useState(false);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const dateRange = useMemo(
    () => computeDateRange(preset, customStart, customEnd),
    [preset, customStart, customEnd]
  );

  const loadData = useCallback(async () => {
    if (preset === 'custom' && (!customStart || !customEnd)) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const params = {
        ...dateRange,
        origin: originFilter,
        service: serviceFilter,
        status: statusFilter,
        group_by: showPerJob ? 'job' : 'none',
      };
      const result = await fetchJobsReport(params);
      setData(result || {});
    } catch (err) {
      setError('Could not load the jobs report. Please try again.');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [dateRange, originFilter, serviceFilter, statusFilter, showPerJob, preset, customStart, customEnd]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const totals = data?.totals || {};
  const byCategory = (data?.by_category || []).filter(c => Number(c.amount) > 0);
  const byOrigin = data?.by_origin || [];
  const byService = data?.by_service || [];
  const perJob = data?.jobs || [];
  const filterOptions = data?.filter_options || { origins: [], services: [] };

  const totalCosts = Number(totals.total_costs) || 0;
  const totalRevenue = Number(totals.revenue) || 0;
  const profit = Number(totals.profit) || (totalRevenue - totalCosts);
  const margin = Number(totals.margin_pct);
  const jobCount = Number(totals.job_count) || 0;

  const maxOriginRevenue = Math.max(1, ...byOrigin.map(o => Number(o.revenue) || 0));
  const maxServiceRevenue = Math.max(1, ...byService.map(s => Number(s.revenue) || 0));

  const toggle = (list, setList, value) => {
    setList(list.includes(value) ? list.filter(v => v !== value) : [...list, value]);
  };

  return (
    <DashboardLayout>
      <Page>
        <Header>
          <h1><FaChartLine /> Jobs Report</h1>
          <Controls>
            <PresetSelect value={preset} onChange={(e) => setPreset(e.target.value)}>
              {PRESETS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
            </PresetSelect>
            {preset === 'custom' && (
              <>
                <DateInput type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} />
                <DateInput type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} />
              </>
            )}
          </Controls>
        </Header>

        <FilterBar>
          <FilterGroup>
            <label><FaMapMarkerAlt /> Lead Origin</label>
            <ChipRow>
              {filterOptions.origins.length === 0 && <span style={{ fontSize: 12, opacity: 0.5 }}>No origins available</span>}
              {filterOptions.origins.map(o => (
                <Chip
                  key={o}
                  active={originFilter.includes(o)}
                  onClick={() => toggle(originFilter, setOriginFilter, o)}
                  type="button"
                >
                  <OriginBadge origin={o} size="sm" />
                </Chip>
              ))}
            </ChipRow>
          </FilterGroup>

          <FilterGroup>
            <label><FaToolbox /> Service</label>
            <ChipRow>
              {filterOptions.services.length === 0 && <span style={{ fontSize: 12, opacity: 0.5 }}>No services available</span>}
              {filterOptions.services.map(s => (
                <Chip
                  key={s}
                  active={serviceFilter.includes(s)}
                  color={serviceColor(s)}
                  onClick={() => toggle(serviceFilter, setServiceFilter, s)}
                  type="button"
                >
                  {s}
                </Chip>
              ))}
            </ChipRow>
          </FilterGroup>

          <FilterGroup>
            <label><FaFilter /> Status</label>
            <ChipRow>
              {STATUS_OPTIONS.map(s => (
                <Chip
                  key={s}
                  active={statusFilter.includes(s)}
                  onClick={() => toggle(statusFilter, setStatusFilter, s)}
                  type="button"
                >
                  {s}
                </Chip>
              ))}
            </ChipRow>
          </FilterGroup>
        </FilterBar>

        {loading ? (
          <LoadingSpinner />
        ) : !data || jobCount === 0 ? (
          <Section>
            <Empty>No jobs match the current filters for this period.</Empty>
          </Section>
        ) : (
          <>
            <MetricsRow>
              <MetricCard accent="#28a745" valueColor="#28a745">
                <div className="label">Revenue</div>
                <div className="value">{formatCurrency(totalRevenue)}</div>
                <div className="sub">Total sold</div>
              </MetricCard>
              <MetricCard accent="#dc3545" valueColor="#dc3545">
                <div className="label">Total Costs</div>
                <div className="value">{formatCurrency(totalCosts)}</div>
                <div className="sub">Materials, labor, fuel…</div>
              </MetricCard>
              <MetricCard accent={profit >= 0 ? '#28a745' : '#dc3545'} valueColor={profit >= 0 ? '#28a745' : '#dc3545'}>
                <div className="label">Profit</div>
                <div className="value">{formatCurrency(profit)}</div>
              </MetricCard>
              <MetricCard accent="#F69620" valueColor="#F69620">
                <div className="label">Margin</div>
                <div className="value">{Number.isFinite(margin) ? `${margin.toFixed(1)}%` : '—'}</div>
              </MetricCard>
              <MetricCard accent="#3f51b5" valueColor="#3f51b5">
                <div className="label">Jobs</div>
                <div className="value">{jobCount}</div>
                <div className="sub">In selection</div>
              </MetricCard>
            </MetricsRow>

            <Section>
              <SectionHeader>
                <h3><FaChartPie /> Cost Breakdown by Category</h3>
              </SectionHeader>
              {byCategory.length === 0 ? (
                <Empty>No costs recorded for the selected jobs.</Empty>
              ) : (
                <DonutBody>
                  <ChartArea>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={byCategory}
                          dataKey="amount"
                          nameKey="category"
                          innerRadius="62%"
                          outerRadius="92%"
                          paddingAngle={2}
                          stroke="none"
                        >
                          {byCategory.map((c) => (
                            <Cell key={c.category} fill={CATEGORY_COLORS[c.category] || FALLBACK_COLOR} />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(value) => formatCurrency(value)}
                          contentStyle={{ background: '#222', border: '1px solid #444', borderRadius: 8 }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                    <CenterText>
                      <div className="label">Total Costs</div>
                      <div className="value">{formatCurrency(totalCosts)}</div>
                    </CenterText>
                  </ChartArea>
                  <Legend>
                    {byCategory.map(c => {
                      const color = CATEGORY_COLORS[c.category] || FALLBACK_COLOR;
                      const pct = Number(c.pct) || (totalCosts > 0 ? (Number(c.amount) / totalCosts) * 100 : 0);
                      return (
                        <LegendRow key={c.category} color={color}>
                          <div className="swatch" />
                          <div className="name">{c.category}</div>
                          <div className="amount">
                            {formatCurrency(c.amount)}
                            <span className="pct">{pct.toFixed(1)}%</span>
                          </div>
                        </LegendRow>
                      );
                    })}
                  </Legend>
                </DonutBody>
              )}
            </Section>

            {byOrigin.length > 0 && (
              <Section>
                <SectionHeader>
                  <h3><FaMapMarkerAlt /> Revenue by Lead Origin</h3>
                </SectionHeader>
                {byOrigin.map(o => {
                  const rev = Number(o.revenue) || 0;
                  const pr = Number(o.profit) || 0;
                  const width = `${(rev / maxOriginRevenue) * 100}%`;
                  return (
                    <BarRow key={o.origin_name}>
                      <BarLabel>
                        <OriginBadge origin={o.origin_name} size="sm" />
                        <span style={{ opacity: 0.6, fontSize: 12 }}>· {o.job_count || 0} jobs</span>
                      </BarLabel>
                      <BarTrack><BarFill color="#3498DB" width={width} /></BarTrack>
                      <BarValue>
                        {formatCurrency(rev)}
                        <span className="sub">Profit {formatCurrency(pr)}</span>
                      </BarValue>
                    </BarRow>
                  );
                })}
              </Section>
            )}

            {byService.length > 0 && (
              <Section>
                <SectionHeader>
                  <h3><FaToolbox /> Revenue by Service</h3>
                </SectionHeader>
                {byService.map(s => {
                  const rev = Number(s.revenue) || 0;
                  const width = `${(rev / maxServiceRevenue) * 100}%`;
                  const color = serviceColor(s.service_name);
                  return (
                    <BarRow key={s.service_name}>
                      <BarLabel>
                        <ServiceBadge color={color}>{s.service_name}</ServiceBadge>
                        <span style={{ opacity: 0.6, fontSize: 12 }}>· {s.job_count || 0} jobs</span>
                      </BarLabel>
                      <BarTrack><BarFill color={color} width={width} /></BarTrack>
                      <BarValue>{formatCurrency(rev)}</BarValue>
                    </BarRow>
                  );
                })}
              </Section>
            )}

            <Section>
              <SectionHeader>
                <h3><FaListUl /> Per-Job Breakdown</h3>
                <Toggle
                  active={showPerJob}
                  onClick={() => setShowPerJob(v => !v)}
                  type="button"
                >
                  {showPerJob ? 'Hide' : 'Show'} per-job rows
                </Toggle>
              </SectionHeader>
              {!showPerJob ? (
                <Empty>Toggle to load a row-per-job breakdown of revenue, costs and profit.</Empty>
              ) : perJob.length === 0 ? (
                <Empty>No jobs in this selection.</Empty>
              ) : (
                <TableWrap>
                  <JobsTable>
                    <thead>
                      <tr>
                        <th>Customer</th>
                        <th>End Date</th>
                        <th>Services</th>
                        <th>Origin</th>
                        <th className="num">Revenue</th>
                        <th className="num">Costs</th>
                        <th className="num">Profit</th>
                        <th className="num">Margin</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {perJob.map(j => {
                        const rev = Number(j.revenue) || 0;
                        const cost = Number(j.total_costs) || 0;
                        const pr = Number(j.profit) || (rev - cost);
                        const m = rev > 0 ? (pr / rev) * 100 : 0;
                        return (
                          <tr key={j.job_id} onClick={() => navigate(`/jobs/${j.job_id}`)}>
                            <td>{j.customer_name || '—'}</td>
                            <td>{j.scheduled_end_date ? format(new Date(j.scheduled_end_date), 'MM/dd/yyyy') : '—'}</td>
                            <td>
                              {(j.services || []).map(s => (
                                <ServiceBadge key={s} color={serviceColor(s)}>{s}</ServiceBadge>
                              ))}
                            </td>
                            <td>{j.origin_name ? <OriginBadge origin={j.origin_name} size="sm" /> : '—'}</td>
                            <td className="num">{formatCurrency(rev)}</td>
                            <td className="num">{formatCurrency(cost)}</td>
                            <td className="num" style={{ color: pr >= 0 ? '#28a745' : '#dc3545' }}>
                              {formatCurrency(pr)}
                            </td>
                            <td className="num">{m.toFixed(1)}%</td>
                            <td><FaChevronRight style={{ opacity: 0.4 }} /></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </JobsTable>
                </TableWrap>
              )}
            </Section>
          </>
        )}

        <FeedbackModal
          isOpen={Boolean(error)}
          message={error}
          success={false}
          onComplete={() => setError('')}
        />
      </Page>
    </DashboardLayout>
  );
};

export default JobsReport;
